// Angular imports
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';

// 3rd party imports
import { DxAccordionComponent } from 'devextreme-angular';

// CC-Common imports
import { IconConstants } from 'cc-common';

// Service imports
import { UserService } from '../../_services/data-services/user.service';
import { CiraHelpService } from '../../_services/shared-services/cira-help.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { UserBiographyService } from '../_services/user-biography.service';

// Constant imports

// Model imports
import { AchievementBadgeResultDto } from '../_models/achievement-badge-result-dto';
import { PortfolioAssignmentsResultDto } from '../_models/portfolio-assignments-result-dto';
import { UserBiographyParametersDto } from '../_models/user-biography-parameters-dto';
import { UserBiographyResultDto } from '../_models/user-biography-result-dto';

interface IUserBioSection {
    id: string;
    title: string;
    icon?: string;
    data?: any;
}

@Component({
    selector: 'app-user-biography',
    templateUrl: './user-biography.component.html',
    styleUrls: ['./user-biography.component.scss']
})
export class UserBiographyComponent {
    @ViewChild('sectionAccordion', { static: false }) sectionAccordion: DxAccordionComponent;

    @Input() mode: 'popup' | 'page' = 'popup';
    @Input() visible = false;

    @Output() onClose: EventEmitter<boolean> = new EventEmitter();

    loadingVisible = false;
    isSmallScreen = false;
    showPortalAssignments = false;
    popupWidth = '45%';
    lockIcon = IconConstants.LOCK;
    iconClose = IconConstants.CLOSE;
    iconDelete = IconConstants.DELETE;
    iconApprove = IconConstants.CHECK;

    userBio: UserBiographyResultDto;
    userBioSections: IUserBioSection[];

    displayPortfolioAssignment = false;
    portfolioAssignmentData: PortfolioAssignmentsResultDto[] = [];
    userId: string;
    portfolioAssignmentTitle: string;

    allowEdit = false;
    allowDelete = false;
    allowApprove = false;
    showUserProfile = false;
    userProfileEditorTitle: string;

    constructor(
        private readonly userService: UserService,
        private readonly screenService: ScreenService,
        private readonly ciraHelpService: CiraHelpService,
        private readonly errorService: ServerErrorService,
        private readonly userBiographyService: UserBiographyService
    ) {
        this.updateScreenSize();
        this.screenService.changed.subscribe(this.updateScreenSize.bind(this));
    }

    show(userId: string, communityId?: number): void {
        this.loadingVisible = true;
        this.userId = userId;
        const params: UserBiographyParametersDto = new UserBiographyParametersDto();
        params.userId = userId;
        params.communityId = communityId;

        this.userBiographyService.getUserBiography(params).subscribe(
            (result) => {
                if (!!result.name) {
                    const userBioSections: IUserBioSection[] = [];
                    this.showPortalAssignments = !!result.assignmentUrl && this.userService.isInternal();
                    // NOTE: Instead of showing portal assignment in Accord we are showing hyperlink in Bio.
                    if (!!result.userBio) {
                        userBioSections.push({
                            id: 'ES',
                            title: 'Experience Summary',
                            data: result.userBio
                        });
                    }
                    if (!!result.degrees && !!result.degrees.length) {
                        userBioSections.push({
                            id: 'ED',
                            title: 'Education',
                            data: result.degrees
                        });
                    }
                    if (!!result.certificates && !!result.certificates.length) {
                        result.certificates.forEach((c) => {
                            c.helpLink = this.ciraHelpService.getHelpLink(null, c.helpKey, communityId);
                        });
                        userBioSections.push({
                            id: 'CL',
                            title: 'Certifications & Licenses',
                            data: result.certificates
                        });
                    }
                    if (!!result.achievements && !!result.achievements.length) {
                        result.achievements.forEach((a) => {
                            a.showRemoteBadgeImage = false;
                            a.remoteBadgeImage = this.userBiographyService.getUserAchivementBadge(a);
                        });
                        userBioSections.push({
                            id: 'AV',
                            title: 'Achievements',
                            data: result.achievements
                        });
                    }
                    this.userBioSections = userBioSections;

                    result.businessRoleHelpLink = this.ciraHelpService.getHelpLink(
                        null,
                        result.businessRoleHelpKey,
                        communityId
                    );
                    this.userBio = result;
                    this.visible = true;
                } else if (!!result.message) {
                    this.errorService.updateSubjectErrorMessage(result.message);
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    hide(): void {
        this.visible = false;
    }

    onShown(e): void {
        e.component.repaint();
        if (!!this.sectionAccordion && !!this.sectionAccordion.instance) {
            this.sectionAccordion.instance.repaint();
        }
    }

    onHidden(): void {
        this.userBio = null;
        this.userBioSections = null;
        this.visible = false;
        this.onClose.emit(true);
    }

    onBadgeImageError(d: AchievementBadgeResultDto): void {
        d.showRemoteBadgeImage = true;
    }

    portalAssignmentClick(): void {
        this.loadingVisible = true;
        this.userBiographyService.getUserPortfolioAssignment(this.userId, true).subscribe(
            (result) => {
                this.portfolioAssignmentData = result;
                this.portfolioAssignmentTitle = `${this.userBio.name} - Assignments`;
                this.displayPortfolioAssignment = true;
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    onEditClick(): void {
        this.userProfileEditorTitle = `Edit Profile for ${this.userBio.name}`;
        this.showUserProfile = true;
    }

    onProfileActionCompleted(): void {
        this.showUserProfile = false;
    }

    private updateScreenSize(): void {
        this.isSmallScreen = this.screenService.isSmallScreen();
        if (this.isSmallScreen) {
            this.popupWidth = '95%';
        } else {
            this.popupWidth = '45%';
        }
    }
}
